import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Notification from "../Notification";
import DOMPurify from 'dompurify';

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  studioName: 100,
  studioEmail: 255,
  studioPhone: 20,
  studioAddress: 500,
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

interface StudioProfile {
  studioName: string;
  studioAddress: string;
  studioPhone: string;
  studioEmail: string;
  timeZone: string;
}

// Enhanced input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // Remove dangerous patterns
  sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
  sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
  sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
  sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
  sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
  
  // For studio names and addresses, preserve spaces but remove excessive whitespace
  if (fieldType === 'studioName' || fieldType === 'studioAddress') {
    sanitized = sanitized.replace(/\s+/g, ' '); // Replace multiple spaces with single space
    return sanitized; // Don't trim for studio names and addresses to preserve intentional spaces
  }
  
  return sanitized.trim();
};

// Enhanced validation function
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'studioName':
      // Allow letters, numbers, spaces, hyphens, apostrophes for studio names
      const nameDangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !nameDangerousPatterns.test(sanitized) && sanitized.length >= 2;
    case 'studioEmail':
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(sanitized) && !sanitized.includes('..');
    case 'studioPhone':
      const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
      return phoneRegex.test(sanitized);
    case 'studioAddress':
      // Allow most characters for addresses, just exclude dangerous ones
      const dangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !dangerousPatterns.test(sanitized) && sanitized.length >= 1;
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

export default function ProfileSettings() {
  const { getAuthToken } = useAuth();
  
  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  
  const [settings, setSettings] = useState<StudioProfile>({
    studioName: "",
    studioAddress: "",
    studioPhone: "",
    studioEmail: "",
    timeZone: "America/New_York",
  });
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({
    studioName: "",
    studioEmail: "",
    studioAddress: "",
    studioPhone: "",
  });
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });

  // Fetch CSRF token
  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  useEffect(() => {
    const fetchStudioInfo = async () => {
      const authToken = getAuthToken();
      if (!authToken) return;

      // Fetch CSRF token first
      await fetchCsrfToken();

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch studio info");
        }
        const data = await response.json();

        setSettings({
          studioName: data.name || "",
          studioAddress: data.address || "",
          studioPhone: data.phone || "",
          studioEmail: data.email || "",
          timeZone: data.timezone || "America/New_York",
        });
      } catch (error) {
        logSecurityEvent('FETCH_STUDIO_INFO_FAILED', { 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
        setNotification({
          show: true,
          message: "Failed to load studio information",
          title: "Error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStudioInfo();
  }, [getAuthToken]);

  // Enhanced validation functions with security checks
  const validateEmail = (email: string): string => {
    if (!email.trim()) {
      return ""; // Email is optional
    }

    const trimmedEmail = email.trim();

    // Check for basic format
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmedEmail)) {
      return "Please enter a valid email address (e.g., yourname@domain.com)";
    }

    // Check length
    if (trimmedEmail.length > MAX_LENGTHS.studioEmail) {
      return `Email address cannot exceed ${MAX_LENGTHS.studioEmail} characters`;
    }

    // Check for consecutive dots
    if (trimmedEmail.includes("..")) {
      return "Email address cannot contain consecutive dots";
    }

    // Check for valid domain
    const domainPart = trimmedEmail.split("@")[1];
    if (domainPart && !domainPart.includes(".")) {
      return "Email domain must contain at least one dot";
    }

    // Security check for dangerous patterns
    if (!validateInput(trimmedEmail, 'studioEmail')) {
      return "Email contains invalid characters";
    }

    return "";
  };

  const validateStudioName = (name: string): string => {
    if (!name.trim()) {
      return "Studio name is required";
    }
    if (name.trim().length < 2) {
      return "Studio name must be at least 2 characters";
    }
    if (name.length > MAX_LENGTHS.studioName) {
      return `Studio name cannot exceed ${MAX_LENGTHS.studioName} characters`;
    }
    
    // Enhanced security check
    if (!validateInput(name, 'studioName')) {
      return "Studio name contains invalid characters";
    }
    return "";
  };

  const validateAddress = (address: string): string => {
    if (address.length > MAX_LENGTHS.studioAddress) {
      return `Address cannot exceed ${MAX_LENGTHS.studioAddress} characters`;
    }
    
    // Enhanced security check
    if (!validateInput(address, 'studioAddress')) {
      return "Address contains invalid characters";
    }
    return "";
  };

  const validatePhone = (phone: string): string => {
    if (!phone.trim()) {
      return ""; // Phone is optional
    }
    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
    if (!phoneRegex.test(phone)) {
      return "Please enter a valid phone number (XXX-XXX-XXXX)";
    }
    
    // Enhanced security check
    if (!validateInput(phone, 'studioPhone')) {
      return "Phone number contains invalid characters";
    }
    return "";
  };

  const handleProfileSettingChange = (
    field: keyof StudioProfile,
    value: string
  ) => {
    let newValue = value;

    if (field === "studioPhone") {
      // Remove all non-digit characters and format
      const digits = value.replace(/\D/g, "");
      if (digits.length <= 3) {
        newValue = digits;
      } else if (digits.length <= 6) {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else if (digits.length <= 10) {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
          6
        )}`;
      } else {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
          6,
          10
        )}`;
      }
    } else {
      // For all other fields, use enhanced sanitization
      newValue = sanitizeInput(value, field);
    }

    // Update settings
    const updatedSettings = {
      ...settings,
      [field]: newValue,
    };
    setSettings(updatedSettings);

    // Validate and set errors
    const newErrors = { ...errors };
    switch (field) {
      case "studioName":
        newErrors.studioName = validateStudioName(newValue);
        break;
      case "studioEmail":
        newErrors.studioEmail = validateEmail(newValue);
        break;
      case "studioAddress":
        newErrors.studioAddress = validateAddress(newValue);
        break;
      case "studioPhone":
        newErrors.studioPhone = validatePhone(newValue);
        break;
    }
    setErrors(newErrors);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'updateProfile' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Comprehensive validation before submission
    const validationErrors = {
      studioName: validateStudioName(settings.studioName),
      studioEmail: validateEmail(settings.studioEmail),
      studioAddress: validateAddress(settings.studioAddress),
      studioPhone: validatePhone(settings.studioPhone),
    };

    setErrors(validationErrors);

    // Check if there are any validation errors
    const hasErrors = Object.values(validationErrors).some(
      (error) => error !== ""
    );
    if (hasErrors) {
      setNotification({
        show: true,
        message: "Please fix the validation errors before saving",
        title: "Validation Error",
      });
      return;
    }

    // Security validation for all fields
    const securityChecks = {
      studioName: validateInput(settings.studioName, 'studioName'),
      studioEmail: settings.studioEmail ? validateInput(settings.studioEmail, 'studioEmail') : true,
      studioAddress: settings.studioAddress ? validateInput(settings.studioAddress, 'studioAddress') : true,
      studioPhone: settings.studioPhone ? validateInput(settings.studioPhone, 'studioPhone') : true,
    };

    const hasSecurityErrors = Object.values(securityChecks).some(check => !check);
    if (hasSecurityErrors) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { 
        field: 'profileUpdate', 
        values: {
          studioName: settings.studioName.substring(0, 50),
          studioEmail: settings.studioEmail.substring(0, 50),
          studioAddress: settings.studioAddress.substring(0, 50),
          studioPhone: settings.studioPhone.substring(0, 50),
        }
      });
      setNotification({
        show: true,
        message: "Invalid input detected. Please check your entries.",
        title: "Security Error",
      });
      return;
    }

    setLoading(true);
    setLastSubmissionTime(now);

    try {
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error("Authentication required");
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            name: sanitizeInput(settings.studioName, 'studioName'),
            address: sanitizeInput(settings.studioAddress, 'studioAddress'),
            phone: sanitizeInput(settings.studioPhone, 'studioPhone'),
            email: sanitizeInput(settings.studioEmail, 'studioEmail'),
            timezone: settings.timeZone,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update profile");
      }

      const data = await response.json();
      setSettings({
        studioName: data.name || "",
        studioAddress: data.address || "",
        studioPhone: data.phone || "",
        studioEmail: data.email || "",
        timeZone: data.timezone || "America/New_York",
      });
      setNotification({
        show: true,
        message: "Profile updated successfully!",
        title: "Success",
      });
    } catch (err) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'updateProfile', 
        error: err instanceof Error ? err.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          err instanceof Error ? err.message : "Failed to update profile",
        title: "Error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Utility function for consistent input styling
  const getInputClassName = (baseClasses: string) => {
    return `${baseClasses} bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-indigo-400 dark:focus:border-indigo-400`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Studio Information
            </h2>
            <div className="text-gray-600 dark:text-gray-400">Loading...</div>
          </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Studio Information
          </h2>
          <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-6">
            <div>
              <label
                htmlFor="studioName"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Studio Name
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="studioName"
                  id="studioName"
                  value={settings.studioName}
                  onChange={(e) =>
                    handleProfileSettingChange("studioName", e.target.value)
                  }
                  placeholder="Enter your studio name"
                  maxLength={MAX_LENGTHS.studioName}
                  className={getInputClassName(
                    `block w-full rounded-md shadow-sm sm:text-sm ${
                      errors.studioName
                        ? "border-red-300 dark:border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                    }`
                  )}
                />
                {errors.studioName && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {errors.studioName}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="studioEmail"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Studio Email
              </label>
              <div className="mt-1">
                <input
                  type="email"
                  name="studioEmail"
                  id="studioEmail"
                  value={settings.studioEmail}
                  onChange={(e) =>
                    handleProfileSettingChange("studioEmail", e.target.value)
                  }
                  placeholder="studio@example.com"
                  maxLength={MAX_LENGTHS.studioEmail}
                  className={getInputClassName(
                    `block w-full rounded-md shadow-sm sm:text-sm ${
                      errors.studioEmail
                        ? "border-red-300 dark:border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                    }`
                  )}
                />
                {errors.studioEmail && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {errors.studioEmail}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="studioPhone"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Studio Phone
              </label>
              <div className="mt-1">
                <input
                  type="tel"
                  name="studioPhone"
                  id="studioPhone"
                  value={settings.studioPhone}
                  onChange={(e) =>
                    handleProfileSettingChange("studioPhone", e.target.value)
                  }
                  placeholder="XXX-XXX-XXXX"
                  maxLength={MAX_LENGTHS.studioPhone}
                  className={getInputClassName(
                    `block w-full rounded-md shadow-sm sm:text-sm ${
                      errors.studioPhone
                        ? "border-red-300 dark:border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                    }`
                  )}
                />
                {errors.studioPhone && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {errors.studioPhone}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="studioAddress"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Studio Address
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="studioAddress"
                  id="studioAddress"
                  value={settings.studioAddress}
                  onChange={(e) =>
                    handleProfileSettingChange("studioAddress", e.target.value)
                  }
                  placeholder="123 Main Street, City, State 12345"
                  maxLength={MAX_LENGTHS.studioAddress}
                  className={getInputClassName(
                    `block w-full rounded-md shadow-sm sm:text-sm ${
                      errors.studioAddress
                        ? "border-red-300 dark:border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                    }`
                  )}
                />
                {errors.studioAddress && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {errors.studioAddress}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="timeZone"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Time Zone
              </label>
              <div className="mt-1">
                <select
                  id="timeZone"
                  name="timeZone"
                  value={settings.timeZone}
                  onChange={(e) =>
                    handleProfileSettingChange("timeZone", e.target.value)
                  }
                  className={getInputClassName(
                    "block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm"
                  )}
                >
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="America/Anchorage">Alaska Time (AKT)</option>
                  <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2 pointer-events-none"></div>
                  <span className="pointer-events-none">Saving...</span>
                </>
              ) : (
                <span className="pointer-events-none">Save Changes</span>
              )}
            </button>
          </div>
        </div>
    </div>
  );
}
